import { ArrowDownward, ArrowUpward, HourglassBottom } from "@mui/icons-material";
import { useMemo } from "react";
import intl from "react-intl-universal";
import { formatCurrency } from "../../../../../Utils/formatters";

const formatSignedSummary = (value, { positivePrefix = "+" } = {}) => {
  const formatted = formatCurrency(Math.abs(value));
  if (value > 0) return `${positivePrefix}${formatted}`;
  if (value < 0) return `-${formatted}`;
  return formatted;
};

const SummaryMetric = ({ icon: Icon, iconClassName, label, value, valueClassName, sublabel }) => (
  <div className="extract-period-summary__metric summary-card min-w-[140px] flex-1 shrink-0">
    <div className="flex min-w-0 items-center gap-2">
      <div className={`summary-card-icon ${iconClassName}`}>
        <Icon sx={{ fontSize: 16 }} aria-hidden />
      </div>
      <p className="min-w-0 flex-1 text-xs leading-snug text-gray-500 dark:text-gray-400">{label}</p>
    </div>
    <p className={`mt-2 break-words text-lg font-bold leading-tight tabular-nums sm:text-xl ${valueClassName}`}>
      {value}
    </p>
    {sublabel ? (
      <p className="mt-1 text-[0.7rem] leading-snug text-gray-400 dark:text-gray-500">{sublabel}</p>
    ) : null}
  </div>
);

export const computeBaasTransactionsTotals = (items = []) => {
  let totalIn = 0;
  let totalOut = 0;

  items.forEach((item) => {
    const amount = Math.abs(Number(item?.amount) || 0);
    if (!amount) return;
    if (item?.direction === "outbound") {
      totalOut += amount;
    } else {
      totalIn += amount;
    }
  });

  return { totalIn, totalOut };
};

export const computeBaasPendingPixTotals = (items = []) => {
  let count = 0;
  let amountCents = 0;
  items.forEach((item) => {
    if (!item?.pendingPix) return;
    count += 1;
    amountCents += Math.abs(Number(item?.amount) || 0);
  });
  return { count, amountCents };
};

export const BaasTransactionsTotalsCards = ({
  items = [],
  pendingItems = null,
  showPendingCard = false,
}) => {
  const summary = useMemo(() => computeBaasTransactionsTotals(items), [items]);
  const pendingSource = pendingItems ?? items;
  const pending = useMemo(
    () => computeBaasPendingPixTotals(pendingSource),
    [pendingSource],
  );

  return (
    <div
      className={`extract-period-summary${showPendingCard ? " extract-period-summary--with-pending" : ""}`}
      role="region"
      aria-label={intl.get("baas.transactions.totalsLabel")}
    >
      <SummaryMetric
        icon={ArrowDownward}
        iconClassName="extract-period-summary__icon--credit"
        label={intl.get("baas.transactions.totals.inbound")}
        value={formatSignedSummary(summary.totalIn)}
        valueClassName="text-emerald-600 dark:text-emerald-400"
      />
      <SummaryMetric
        icon={ArrowUpward}
        iconClassName="extract-period-summary__icon--debit"
        label={intl.get("baas.transactions.totals.outbound")}
        value={formatSignedSummary(summary.totalOut, { positivePrefix: "-" })}
        valueClassName="text-red-600 dark:text-red-400"
      />
      {showPendingCard ? (
        <SummaryMetric
          icon={HourglassBottom}
          iconClassName="extract-period-summary__icon--pending"
          label={intl.get("baas.transactions.totals.pending")}
          value={String(pending.count)}
          valueClassName="text-brand-dark dark:text-gray-100"
          sublabel={
            pending.count > 0
              ? intl.get("baas.transactions.totals.pendingAmount", {
                  amount: formatCurrency(pending.amountCents),
                })
              : intl.get("baas.transactions.totals.pendingEmpty")
          }
        />
      ) : null}
    </div>
  );
};
